import { useEffect, useRef } from 'react'
import { useMarkSummaryAsRead, useSummaryDate } from '@renderer/chat'
import { AmplitudeRenderer } from '@renderer/utils/amplitude.renderer'
import { useSummaryContext } from './use-summary-context'

export function useSummaryMarkAsRead(forceExpand?: boolean) {
  const { summary, isExpanded, hasInteracted } = useSummaryContext()
  const markAsRead = useMarkSummaryAsRead()
  const { isToday, dayLabel } = useSummaryDate(summary)
  const hasMarkedAsRead = useRef(false)

  useEffect(() => {
    // Skip if already marked or if summary is already read
    if (hasMarkedAsRead.current || summary.read) {
      return
    }

    // Mark as read if:
    // 1. Summary is expanded AND
    // 2. Either the summary was already read OR user has interacted with it
    const shouldMarkAsRead = isExpanded && (summary.read || hasInteracted)

    if (shouldMarkAsRead) {
      // Add 1-second delay before marking as read
      const timer = setTimeout(() => {
        hasMarkedAsRead.current = true
        markAsRead
          .mutateAsync(summary.id)
          .then(() => {
            AmplitudeRenderer.trackEvent('Summary Action', {
              type: 'Read',
              timePeriod: isToday ? 'Today' : dayLabel,
            })
          })
          .catch((error) => {
            // Reset flag on error so we can retry
            hasMarkedAsRead.current = false
            console.error('Failed to mark summary as read:', error)
          })
      }, 1000)

      return () => clearTimeout(timer)
    }
  }, [summary.id, summary.read, isExpanded, hasInteracted, markAsRead, isToday, dayLabel])
}
